/**
 * PropertyEditor — Unified modal for creating and editing properties.
 * Replaces both CreatePropertyModal and PropertyDetailModal.
 *
 * Modes:
 *  - "create" — New property, base64 ImageUpload, single create mutation
 *  - "edit"   — Existing property, R2 PropertyImageGallery + AITools, view/edit toggle
 */
import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Home,
  MapPin,
  Maximize,
  Banknote,
  BedDouble,
  Bath,
  DoorOpen,
  Pencil,
  Trash2,
  Loader2,
  Image as ImageIcon,
  History,
  PartyPopper,
  Trophy,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useAuthStore } from '@/stores/auth';
import { properties, ai, type Property } from '@/lib/api';
import { formatCurrency, statusLabels, statusColors, propertyTypes } from '@/lib/utils';
import { useProofreader } from '@/hooks/useProofreader';
import { PropertyForm, initialFormData, toPropertyData, type PropertyFormData } from './PropertyForm';
import { PropertyImageGallery } from './PropertyImageGallery';
import { AITools } from './AITools';
import { ImageUpload } from './ImageUpload';
import { PhotoScoring } from './PhotoScoring';

// ═══════════════════════════════════════════
// Types
// ═══════════════════════════════════════════

interface PropertyEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null for create mode, Property object for edit mode */
  property: Property | null;
  /** Explicit mode override. Defaults to 'create' when property is null, 'edit' otherwise */
  mode?: 'create' | 'edit';
}

// ═══════════════════════════════════════════
// Component
// ═══════════════════════════════════════════

export function PropertyEditor({
  open,
  onOpenChange,
  property,
  mode: explicitMode,
}: PropertyEditorProps) {
  const mode = explicitMode ?? (property ? 'edit' : 'create');
  const { token } = useAuthStore();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Form state
  const [isEditMode, setIsEditMode] = useState(mode === 'create');
  const [formData, setFormData] = useState<PropertyFormData>(initialFormData);
  const [activeTab, setActiveTab] = useState<string>('overview');

  // Edit-mode specific
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSoldCelebration, setShowSoldCelebration] = useState(false);
  const [celebrationImage, setCelebrationImage] = useState<string | null>(null);
  const [markingSold, setMarkingSold] = useState(false);

  const proofreader = useProofreader(formData.description, token);

  // ─── Populate form when property/mode changes ───
  useEffect(() => {
    if (!open) return;

    if (mode === 'create') {
      setFormData(initialFormData);
      setIsEditMode(true);
      setActiveTab('overview');
    } else if (property) {
      setFormData({
        address: property.address || '',
        postal_code: property.postal_code || '',
        city: property.city || '',
        property_type: property.property_type || 'íbúð',
        size_sqm: property.size_sqm?.toString() || '',
        rooms: property.rooms?.toString() || '',
        bedrooms: property.bedrooms?.toString() || '',
        bathrooms: property.bathrooms?.toString() || '',
        price: property.price ? property.price.toLocaleString('is-IS') : '',
        description: property.description || '',
        status: property.status || 'á_markaði',
        images: property.images || [],
      });
      setIsEditMode(false);
      setActiveTab('overview');
    }
  }, [open, property, mode]);

  // ─── Mutations ───
  const createMutation = useMutation({
    mutationFn: (data: Partial<Property>) => properties.create(token!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      toast({ title: 'Eign stofnuð', description: 'Nýja eignin hefur verið skráð.', variant: 'success' });
      handleClose();
    },
    onError: (error) => {
      toast({
        title: 'Villa',
        description: error instanceof Error ? error.message : 'Ekki tókst að stofna eign.',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Property>) => properties.update(token!, property!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      toast({ title: 'Eign uppfærð', description: 'Upplýsingar um eign hafa verið vistaðar.', variant: 'success' });
      setIsEditMode(false);
    },
    onError: (error) => {
      toast({
        title: 'Villa',
        description: error instanceof Error ? error.message : 'Ekki tókst að uppfæra eign.',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => properties.delete(token!, property!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      toast({ title: 'Eign eytt', description: 'Eignin hefur verið eytt.', variant: 'success' });
      setShowDeleteConfirm(false);
      onOpenChange(false);
    },
    onError: (error) => {
      toast({
        title: 'Villa',
        description: error instanceof Error ? error.message : 'Ekki tókst að eyða eign.',
        variant: 'destructive',
      });
    },
  });

  // ─── Handlers ───
  const handleClose = () => {
    if (mode === 'create') setFormData(initialFormData);
    setIsEditMode(mode === 'create');
    onOpenChange(false);
  };

  const handleChange = (field: keyof PropertyFormData, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.address.trim()) {
      toast({ title: 'Villa', description: 'Heimilisfang er skyldureitur.', variant: 'destructive' });
      return;
    }

    const data = toPropertyData(formData);

    if (mode === 'create') {
      createMutation.mutate({
        ...data,
        images: formData.images.length > 0 ? formData.images : undefined,
      });
    } else {
      updateMutation.mutate(data);
    }
  };

  const handleCancelEdit = () => {
    if (mode === 'create') {
      handleClose();
      return;
    }
    if (property) {
      setFormData({
        address: property.address || '',
        postal_code: property.postal_code || '',
        city: property.city || '',
        property_type: property.property_type || 'íbúð',
        size_sqm: property.size_sqm?.toString() || '',
        rooms: property.rooms?.toString() || '',
        bedrooms: property.bedrooms?.toString() || '',
        bathrooms: property.bathrooms?.toString() || '',
        price: property.price ? property.price.toLocaleString('is-IS') : '',
        description: property.description || '',
        status: property.status || 'á_markaði',
        images: property.images || [],
      });
    }
    setIsEditMode(false);
  };

  const handleMarkSold = async () => {
    if (!token || !property) return;
    setMarkingSold(true);
    try {
      await properties.update(token, property.id, { status: 'selt' });
      queryClient.invalidateQueries({ queryKey: ['properties'] });

      let celebImg: string | null = null;
      try {
        const agentName = property.agent_name || 'sölumaðurinn';
        const addr = property.address || 'fasteignina';
        const result = await ai.generateCelebration(token, agentName, addr, property.property_type || 'íbúð');
        if (result.success && result.data?.image_data) {
          celebImg = result.data.image_data;
        }
      } catch {
        // Celebration image is optional
      }

      setCelebrationImage(celebImg);
      setShowSoldCelebration(true);
      toast({ title: '🎉 Eign seld!', description: `${property.address} hefur verið merkt sem seld.`, variant: 'success' });
    } catch (error) {
      toast({
        title: 'Villa',
        description: error instanceof Error ? error.message : 'Ekki tókst að merkja eign sem selda.',
        variant: 'destructive',
      });
    } finally {
      setMarkingSold(false);
    }
  };

  const isPending = mode === 'create' ? createMutation.isPending : updateMutation.isPending;

  // ═══════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Home className="w-5 h-5 text-prima-600" />
                {mode === 'create'
                  ? 'Ný eign'
                  : isEditMode
                    ? 'Breyta eign'
                    : 'Upplýsingar um eign'}
              </DialogTitle>
              {mode === 'edit' && !isEditMode && property && (
                <div className="flex items-center gap-2">
                  {property.status !== 'selt' && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleMarkSold}
                      disabled={markingSold}
                      className="gap-1 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white shadow-md"
                    >
                      {markingSold ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />}
                      Selt!
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setIsEditMode(true)}>
                    <Pencil className="w-4 h-4 mr-1" />
                    Breyta
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => setShowDeleteConfirm(true)}>
                    <Trash2 className="w-4 h-4 mr-1" />
                    Eyða
                  </Button>
                </div>
              )}
            </div>
            <DialogDescription>
              {mode === 'create'
                ? 'Skráðu nýja eign inn í kerfið. Reitir með * eru skyldusvæði.'
                : isEditMode
                  ? 'Breyttu upplýsingunum og smelltu á Vista.'
                  : 'Skoðaðu og breyttu upplýsingunum um eignina.'}
            </DialogDescription>
          </DialogHeader>

          {isEditMode ? (
            <EditFormView
              mode={mode}
              formData={formData}
              onChange={handleChange}
              proofreader={proofreader}
              property={property}
              isPending={isPending}
              onSubmit={handleSubmit}
              onCancel={handleCancelEdit}
              onImagesReorder={async (orderedImages) => {
                if (!token || !property) return;
                try {
                  await properties.update(token, property.id, { images: orderedImages });
                  queryClient.invalidateQueries({ queryKey: ['properties'] });
                } catch {
                  toast({ title: 'Villa', description: 'Ekki tókst að endurraða myndum.', variant: 'destructive' });
                }
              }}
            />
          ) : (
            property && (
              <ReadOnlyView
                property={property}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                onClose={handleClose}
              />
            )
          )}
        </DialogContent>
      </Dialog>

      {/* Sold Celebration Dialog (edit mode only) */}
      {mode === 'edit' && property && (
        <SoldCelebrationDialog
          open={showSoldCelebration}
          onOpenChange={setShowSoldCelebration}
          property={property}
          celebrationImage={celebrationImage}
        />
      )}

      {/* Delete Confirmation (edit mode only) */}
      {mode === 'edit' && (
        <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Eyða eign?</AlertDialogTitle>
              <AlertDialogDescription>
                Ertu viss um að þú viljir eyða þessari eign? Þetta er ekki hægt að afturkalla.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteMutation.isPending}>Hætta við</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteMutation.isPending ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Eyði...</>
                ) : (
                  'Eyða eign'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}

// ═══════════════════════════════════════════
// Sub-components (private to this file)
// ═══════════════════════════════════════════

/** Edit form — used in both create and edit modes */
function EditFormView({
  mode,
  formData,
  onChange,
  proofreader,
  property,
  isPending,
  onSubmit,
  onCancel,
  onImagesReorder,
}: {
  mode: 'create' | 'edit';
  formData: PropertyFormData;
  onChange: (field: keyof PropertyFormData, value: string | string[]) => void;
  proofreader: ReturnType<typeof useProofreader>;
  property: Property | null;
  isPending: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  onImagesReorder?: (orderedImages: string[]) => void;
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <PropertyForm formData={formData} onChange={onChange} proofreader={proofreader}>
        {/* Mode-specific image handling */}
        {mode === 'create' ? (
          <ImageUpload
            images={formData.images}
            onChange={(images) => onChange('images', images)}
            maxImages={10}
            maxSizeMB={5}
          />
        ) : property ? (
          <>
            <PropertyImageGallery
              propertyId={property.id}
              images={property.images || []}
              imageCaptions={property.image_captions || {}}
              maxImages={10}
              maxSizeMB={10}
            />
            <AITools
              propertyId={property.id}
              images={property.images || []}
              propertyType={formData.property_type}
              propertyData={{
                size_sqm: formData.size_sqm ? Number(formData.size_sqm) : undefined,
                location: [formData.address, formData.postal_code, formData.city].filter(Boolean).join(', ') || undefined,
                property_type: formData.property_type,
                price: formData.price ? Number(formData.price.replace(/\D/g, '')) : undefined,
              }}
              onDescriptionGenerated={(desc) => onChange('description', desc)}
            />
            <PhotoScoring
              propertyId={property.id}
              images={property.images || []}
              imageScores={property.image_scores}
              onReorder={onImagesReorder}
            />
          </>
        ) : null}
      </PropertyForm>

      <DialogFooter className="gap-2 sm:gap-0">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
          Hætta við
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{mode === 'create' ? 'Stofna...' : 'Vista...'}</>
          ) : (
            mode === 'create' ? 'Stofna eign' : 'Vista breytingar'
          )}
        </Button>
      </DialogFooter>
    </form>
  );
}

/** Read-only view with tabs — only for edit mode */
function ReadOnlyView({
  property,
  activeTab,
  onTabChange,
  onClose,
}: {
  property: Property;
  activeTab: string;
  onTabChange: (tab: string) => void;
  onClose: () => void;
}) {
  return (
    <Tabs value={activeTab} onValueChange={onTabChange} className="w-full">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="overview" className="flex items-center gap-2">
          <Home className="w-4 h-4" />
          <span className="hidden sm:inline">Yfirlit</span>
        </TabsTrigger>
        <TabsTrigger value="images" className="flex items-center gap-2">
          <ImageIcon className="w-4 h-4" />
          <span className="hidden sm:inline">Myndir</span>
        </TabsTrigger>
        <TabsTrigger value="history" className="flex items-center gap-2">
          <History className="w-4 h-4" />
          <span className="hidden sm:inline">Saga</span>
        </TabsTrigger>
      </TabsList>

      {/* Overview Tab */}
      <TabsContent value="overview" className="space-y-6 mt-6">
        {property.status === 'selt' && (
          <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 p-4 text-white shadow-lg">
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/10 rounded-full" />
            <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-white/10 rounded-full" />
            <div className="flex items-center gap-3 relative z-10">
              <Trophy className="w-8 h-8 text-yellow-300" />
              <div>
                <p className="text-lg font-bold">SELT!</p>
                <p className="text-sm text-emerald-100">{property.address} hefur verið seld.</p>
              </div>
              <PartyPopper className="w-6 h-6 text-yellow-300 ml-auto" />
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          <span className={statusColors[property.status] || 'badge-gray'}>
            {statusLabels[property.status] || property.status}
          </span>
          <span className="text-sm text-gray-500">
            {propertyTypes[property.property_type] || property.property_type}
          </span>
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-medium text-gray-500 flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Staðsetning
          </h3>
          <p className="text-lg font-semibold text-gray-900">{property.address}</p>
          {(property.postal_code || property.city) && (
            <p className="text-gray-600">{property.postal_code} {property.city}</p>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {property.size_sqm && (
            <StatCard icon={<Maximize className="w-4 h-4" />} label="Stærð" value={`${property.size_sqm} m²`} />
          )}
          {property.rooms && (
            <StatCard icon={<DoorOpen className="w-4 h-4" />} label="Herbergi" value={String(property.rooms)} />
          )}
          {property.bedrooms && (
            <StatCard icon={<BedDouble className="w-4 h-4" />} label="Svefnherbergi" value={String(property.bedrooms)} />
          )}
          {property.bathrooms && (
            <StatCard icon={<Bath className="w-4 h-4" />} label="Baðherbergi" value={String(property.bathrooms)} />
          )}
        </div>

        {property.price && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <Banknote className="w-4 h-4" /> Verð
            </h3>
            <p className="text-2xl font-bold text-prima-600">{formatCurrency(property.price)}</p>
          </div>
        )}

        {property.description && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-500">Lýsing</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{property.description}</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Loka</Button>
        </DialogFooter>
      </TabsContent>

      {/* Images Tab */}
      <TabsContent value="images" className="space-y-6 mt-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Myndir af eign ({property.images?.length || 0})
            </h3>
          </div>
          <PropertyImageGallery
            propertyId={property.id}
            images={property.images || []}
            imageCaptions={property.image_captions || {}}
            maxImages={10}
            maxSizeMB={10}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Loka</Button>
        </DialogFooter>
      </TabsContent>

      {/* History Tab */}
      <TabsContent value="history" className="space-y-6 mt-6">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Saga eignar</h3>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
            <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">Söguyfirlit í vinnslu</p>
            <p className="text-sm text-gray-400">
              PropertyHistory komponent verður bætt við þegar það er tilbúið
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Loka</Button>
        </DialogFooter>
      </TabsContent>
    </Tabs>
  );
}

/** Small stat card for the overview grid */
function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="flex items-center gap-1 text-gray-500 text-sm mb-1">{icon} {label}</div>
      <p className="font-semibold text-gray-900">{value}</p>
    </div>
  );
}

/** Sold celebration popup */
function SoldCelebrationDialog({
  open,
  onOpenChange,
  property,
  celebrationImage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  property: Property;
  celebrationImage: string | null;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] overflow-hidden">
        <div className="text-center space-y-4 py-4">
          <div className="relative">
            <div className="absolute inset-0 flex justify-center pointer-events-none overflow-hidden">
              {Array.from({ length: 20 }).map((_, i) => (
                <div
                  key={i}
                  className="absolute w-2 h-2 rounded-full animate-bounce"
                  style={{
                    backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FF69B4', '#FF8C00'][i % 7],
                    left: `${5 + (i * 4.5)}%`,
                    top: `${-10 - (i % 5) * 10}%`,
                    animationDelay: `${i * 0.1}s`,
                    animationDuration: `${1.5 + (i % 3) * 0.5}s`,
                  }}
                />
              ))}
            </div>
            <div className="flex justify-center gap-2 text-5xl mb-4 animate-bounce">
              🎉🏆🥂
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Til hamingju!</h2>
          <p className="text-lg text-gray-600">
            <span className="font-semibold text-emerald-600">{property.address}</span> hefur verið seld!
          </p>
          {property.price && (
            <p className="text-3xl font-bold text-emerald-600">{formatCurrency(property.price)}</p>
          )}

          {celebrationImage ? (
            <div className="relative rounded-xl overflow-hidden shadow-lg mx-auto max-w-[300px]">
              <img src={celebrationImage} alt="Fagnaður!" className="w-full h-auto" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                <p className="text-white text-xs font-medium">AI búið til fagnað 🤖🍾</p>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-r from-yellow-50 to-emerald-50 rounded-xl p-6 mx-auto max-w-[300px]">
              <div className="text-6xl mb-2">🍾</div>
              <p className="text-sm text-gray-500 italic">
                "{property.agent_name || 'Sölumaðurinn'} seldi eignina með glæsibrag!"
              </p>
            </div>
          )}

          <Button
            onClick={() => onOpenChange(false)}
            className="mt-4 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white"
          >
            <PartyPopper className="w-4 h-4 mr-2" />
            Frábært!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
